<script>
  // The drop planner, kept small: a named launch with a date and time, and the products that go on sale then. Detailed planning happens elsewhere.
  import { onMount } from 'svelte';
  import Button from '../ui/Button.svelte';
  import Card from '../ui/Card.svelte';
  import Badge from '../ui/Badge.svelte';
  import Tabs from '../ui/Tabs.svelte';
  import Sheet from '../ui/Sheet.svelte';
  import Switch from '../ui/Switch.svelte';
  import DatePicker from '../ui/DatePicker.svelte';
  import TimePicker from '../ui/TimePicker.svelte';
  import DropDetail from './DropDetail.svelte';
  import { inputCls, labelCls, hintCls } from '../../lib/ui.js';
  import { toast } from '../../lib/toast.js';
  import { confirmDialog } from '../../lib/confirm.js';
  import { DROP_STATUS, dropApi, fmtDate, launchText } from '../../lib/drops.js';
  import Plus from 'lucide-svelte/icons/plus';
  import Rocket from 'lucide-svelte/icons/rocket';
  import LoaderCircle from 'lucide-svelte/icons/loader-circle';
  import Bell from 'lucide-svelte/icons/bell';
  import CalendarPlus from 'lucide-svelte/icons/calendar-plus';
  import Copy from 'lucide-svelte/icons/copy';
  import { selectCls } from '../../lib/ui.js';
  import PushDevices from './PushDevices.svelte';

  let { site = {}, products = [], onProductsChanged = () => {} } = $props();
  const currency = $derived(site?.currency || 'R');

  let drops = $state([]);
  let today = $state('');
  let loading = $state(true);
  let loadError = $state('');
  let filter = $state('active');
  let openId = $state(null);

  async function load() {
    loading = true; loadError = '';
    try { const r = await dropApi('GET', '/api/drops'); drops = r.drops; today = r.today; }
    catch (e) { loadError = e.message; }
    finally { loading = false; }
  }
  onMount(load);
  const upsert = (d) => { drops = drops.some(x => x.id === d.id) ? drops.map(x => (x.id === d.id ? d : x)) : [...drops, d].sort((a, b) => a.launchDate.localeCompare(b.launchDate)); };

  const isClosed = (d) => ['wrapped', 'cancelled'].includes(d.status);
  const active = $derived(drops.filter(d => !isClosed(d)));
  const tabItems = $derived([
    { value: 'active', label: 'Active', count: active.length },
    { value: 'closed', label: 'Wrapped up', count: drops.length - active.length },
    { value: 'all', label: 'All', count: drops.length },
  ]);
  const shown = $derived(drops.filter(d => (filter === 'all' ? true : filter === 'active' ? !isClosed(d) : isClosed(d))));

  // ── New drop ───────────────────────────────────────────────────────────────────
  let createOpen = $state(false);
  let form = $state({ name: '', launchDate: '', launchTime: '09:00', productIds: [], featureOnLaunch: true });
  let creating = $state(false);
  function openCreate() {
    const d = new Date(`${today}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 14);
    form = { name: '', launchDate: d.toISOString().slice(0, 10), launchTime: '09:00', productIds: [], featureOnLaunch: true };
    createOpen = true;
  }
  async function create() {
    if (creating) return;
    creating = true;
    try {
      let d = await dropApi('POST', '/api/drops', { name: form.name, launchDate: form.launchDate, deliveryDate: form.deliveryDate, template: 'blank', launchTime: form.launchTime, featureOnLaunch: form.featureOnLaunch });
      if (form.productIds.length) { d = await dropApi('POST', `/api/drops/${d.id}/products`, { productIds: form.productIds }); onProductsChanged(); }
      upsert(d); createOpen = false; openId = d.id; filter = 'active';
      toast.success(form.productIds.length ? 'Drop created. Its products are hidden from the shop until the launch.' : 'Drop created');
    } catch (e) { toast.error(e.message); }
    finally { creating = false; }
  }


  // ── Reminders and the calendar link ────────────────────────────────────────────
  let cfgOpen = $state(false);
  let cfg = $state(null);                     // what the server has: alerts, calendarUrl, emailConfigured, defaultRecipients
  let alerts = $state({ enabled: false, hour: 8, daysAhead: 3, recipients: '' });
  let cfgBusy = $state('');
  async function openConfig() {
    cfgOpen = true;
    try { cfg = await dropApi('GET', '/api/drops/config'); alerts = { ...cfg.alerts }; }
    catch (e) { toast.error(e.message); cfgOpen = false; }
  }
  async function cfgRun(name, fn) {
    if (cfgBusy) return;
    cfgBusy = name;
    try { await fn(); } catch (e) { toast.error(e.message); } finally { cfgBusy = ''; }
  }
  const saveAlerts = () => cfgRun('save', async () => { const r = await dropApi('PUT', '/api/drops/config', { alerts }); alerts = { ...r.alerts }; cfg = { ...cfg, alerts: r.alerts }; toast.success(alerts.enabled ? 'Launch alerts are on' : 'Saved'); });
  // This device just signed up: make sure the reminder uses push.
  async function pushEnabled() {
    if (alerts.push) return;
    alerts.push = true;
    if (alerts.enabled) { try { const r = await dropApi('PUT', '/api/drops/config', { alerts }); alerts = { ...r.alerts }; } catch (e) { toast.error(e.message); } }
  }
  const sendTest = () => cfgRun('test', async () => { const saved = await dropApi('PUT', '/api/drops/config', { alerts }); alerts = { ...saved.alerts };       // test what is on screen
    const r = await dropApi('POST', '/api/drops/digest/send'); toast.success(`Sent: ${[r.via.push && `${r.via.push} device${r.via.push === 1 ? '' : 's'}`, r.via.email.length && r.via.email.join(', ')].filter(Boolean).join(' and ')}${r.problems?.length ? `. ${r.problems.join(' ')}` : ''}`); });
  const makeLink = () => cfgRun('link', async () => {
    if (cfg.calendarUrl && !(await confirmDialog.ask({ title: 'Make a new link?', description: 'The old link stops working. Calendars that use it will need the new one.', confirmLabel: 'Make a new link' }))) return;
    cfg = { ...cfg, ...(await dropApi('POST', '/api/drops/config/calendar-link')) };
  });
  const stopLink = () => cfgRun('link', async () => {
    if (!(await confirmDialog.ask({ title: 'Turn the link off?', description: 'Calendars that use it will stop updating.', confirmLabel: 'Turn off', destructive: true }))) return;
    await dropApi('DELETE', '/api/drops/config/calendar-link');
    cfg = { ...cfg, calendarUrl: '' };
  });
  async function copyLink() {
    try { await navigator.clipboard.writeText(cfg.calendarUrl); toast.success('Link copied'); }
    catch { document.getElementById('cal-url')?.select(); toast.info('Press Ctrl+C to copy the selected link'); }
  }

  const current = $derived(openId ? drops.find(d => d.id === openId) : null);
  $effect(() => { if (openId && !loading && !current) openId = null; });
</script>

{#if current}
  <DropDetail drop={current} {products} {currency} {onProductsChanged} onChange={upsert} onBack={() => (openId = null)} onDeleted={(id) => { drops = drops.filter(d => d.id !== id); openId = null; }} />
{:else}
  <div class="space-y-6">
    <div class="flex items-end justify-between gap-4">
      <div>
        <h1 class="text-2xl font-semibold tracking-tight">Drop planner</h1>
        <p class="text-sm text-muted-foreground">Name a launch, pick its products, and they go on sale by themselves on the day. For a quick release you can also use Schedule release in Products.</p>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <Button variant="outline" onclick={openConfig} disabled={loading || !!loadError}><Bell size={16} /> Reminders</Button>
        <Button onclick={openCreate} disabled={loading || !!loadError}><Plus size={16} /> New drop</Button>
      </div>
    </div>

    {#if loading}
      <div class="flex items-center justify-center gap-2 py-24 text-sm text-muted-foreground" role="status"><LoaderCircle size={16} class="animate-spin" /> Loading drops…</div>
    {:else if loadError}
      <Card><div class="flex flex-col items-center gap-3 py-16 text-center"><p class="text-sm text-destructive">{loadError}</p><Button variant="outline" onclick={load}>Try again</Button></div></Card>
    {:else}
      {#if drops.length}
        <div class="overflow-x-auto"><Tabs items={tabItems} bind:value={filter} label="Filter drops" /></div>
      {/if}

      {#if shown.length === 0}
        <Card><div class="flex flex-col items-center gap-2 py-16 text-center">
          <Rocket size={28} class="text-muted-foreground" />
          <p class="font-medium">{drops.length ? 'No drops here' : 'No drops yet'}</p>
          <p class="max-w-md text-sm text-muted-foreground">{drops.length ? 'Try another tab.' : 'Pick a launch date and time and the products that go with it.'}</p>
          {#if !drops.length}<Button class="mt-2" onclick={openCreate}><Plus size={16} /> Create your first drop</Button>{/if}
        </div></Card>
      {:else}
        <div class="grid gap-4 md:grid-cols-2">
          {#each shown as d (d.id)}
            <button type="button" onclick={() => (openId = d.id)} class="group rounded-xl border border-border bg-card p-5 text-left shadow-xs transition-colors hover:bg-muted/40 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/50">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <h2 class="truncate font-semibold tracking-tight">{d.name}</h2>
                  <p class="mt-0.5 text-sm text-muted-foreground">{fmtDate(d.launchDate)} at {d.launchTime || '09:00'}{#if !isClosed(d)} · {launchText(d.daysToLaunch)}{/if}</p>
                </div>
                <Badge variant="outline">{['planning', 'in_production', 'ready'].includes(d.status) ? 'Scheduled' : DROP_STATUS[d.status]}</Badge>
              </div>
              <p class="mt-3 text-xs text-muted-foreground">{d.styles.length} product{d.styles.length === 1 ? '' : 's'}{#if d.featureOnLaunch} · Featured on the homepage at launch{/if}</p>
            </button>
          {/each}
        </div>

      {/if}
    {/if}
  </div>
{/if}

<Sheet bind:open={createOpen} title="New drop" description="Pick when it goes on sale and which products go with it.">
  <form class="space-y-5" onsubmit={(e) => { e.preventDefault(); create(); }}>
    <div><label class={labelCls} for="nd-name">Name</label><input id="nd-name" class={inputCls} bind:value={form.name} maxlength="80" placeholder="e.g. Summer capsule" required /></div>
    <div class="grid grid-cols-2 gap-4">
      <div><label class={labelCls} for="nd-launch">Date</label><DatePicker id="nd-launch" bind:value={form.launchDate} min={today} /></div>
      <div><label class={labelCls} for="nd-time">Time</label><TimePicker id="nd-time" bind:value={form.launchTime} /></div>
    </div>
    <div class="flex items-center justify-between gap-4">
      <div class="text-sm"><p class="font-medium">Feature on the homepage when it launches</p><p class="text-muted-foreground">Its products lead the homepage's featured strip from launch time.</p></div>
      <Switch bind:checked={form.featureOnLaunch} aria-label="Feature on the homepage when it launches" />
    </div>
    <fieldset>
      <legend class={labelCls}>Products <span class="font-normal text-muted-foreground">(optional, you can add them later)</span></legend>
      {#if products.length}
        <div class="max-h-56 overflow-y-auto rounded-md border border-border">
          {#each products as p (p.id)}
            <label class="flex items-center gap-2.5 border-b border-border/60 px-3 py-2 text-sm last:border-0 hover:bg-muted/40"><input type="checkbox" class="h-4 w-4 rounded border-input" value={p.id} bind:group={form.productIds} />{p.name}{#if p.hidden}<span class="text-xs text-muted-foreground">(draft)</span>{/if}</label>
          {/each}
        </div>
        <p class={hintCls}>They stay hidden from your shop and go on sale, tagged New, at the time above.</p>
      {:else}<p class={hintCls}>Add products in Products first, then add them to the drop.</p>{/if}
    </fieldset>
    <div class="flex justify-end gap-2 pt-2"><Button variant="outline" onclick={() => (createOpen = false)}>Cancel</Button><Button type="submit" disabled={creating || !form.name.trim() || !form.launchDate || !form.launchTime}>{#if creating}<LoaderCircle size={14} class="animate-spin" />{/if} Create drop</Button></div>
  </form>
</Sheet>

<Sheet bind:open={cfgOpen} title="Reminders &amp; calendar" description="Get told when a drop goes live, and see your launches next to everything else.">
  {#if cfg}
    <div class="space-y-8">
      <section class="space-y-4" aria-labelledby="rem-h">
        <h3 id="rem-h" class="flex items-center gap-2 font-semibold"><Bell size={16} /> Launch alerts</h3>
        <p class="text-sm text-muted-foreground">Hear about it when a drop goes live by itself.</p>
        <label class="flex items-center gap-2.5 text-sm font-medium"><input type="checkbox" bind:checked={alerts.enabled} class="h-4 w-4 rounded border-input" /> Tell me when a drop launches</label>

        <fieldset class="space-y-4 rounded-lg border border-border p-4">
          <legend class="px-1 text-sm font-medium">How should it reach you?</legend>

          <div class="space-y-3">
            <label class="flex items-start gap-2.5 text-sm"><input type="checkbox" bind:checked={alerts.push} class="mt-0.5 h-4 w-4 rounded border-input" />
              <span><span class="font-medium">A notification on my devices</span> <span class="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300">Free</span><span class="block text-muted-foreground">Appears on your phone or computer like any app notification. Sent through your browser's own push service, so there is nothing to pay.</span></span></label>
            {#if alerts.push}
              <div class="ml-6"><PushDevices onEnabled={pushEnabled} /></div>
            {/if}
          </div>

          <div class="space-y-3 border-t border-border pt-4">
            <label class="flex items-start gap-2.5 text-sm"><input type="checkbox" bind:checked={alerts.email} class="mt-0.5 h-4 w-4 rounded border-input" />
              <span><span class="font-medium">An email</span><span class="block text-muted-foreground">Useful as a back-up. Counts towards your email sending.</span></span></label>
            {#if alerts.email}
              <div class="ml-6 space-y-2">
                {#if !cfg.emailConfigured}<p role="alert" class="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">Email is not set up on this server, so this cannot be sent yet.</p>{/if}
                <div><label class={labelCls} for="al-to">Send to</label><input id="al-to" class={inputCls} bind:value={alerts.recipients} placeholder={cfg.defaultRecipients.join(', ')} />
                  <p class={hintCls}>Separate addresses with commas. Leave it empty to use your system alert addresses{cfg.defaultRecipients.length ? ` (${cfg.defaultRecipients.join(', ')})` : ''}.</p></div>
              </div>
            {/if}
          </div>
        </fieldset>

        <div class="flex flex-wrap gap-2">
          <Button onclick={saveAlerts} disabled={!!cfgBusy}>{#if cfgBusy === 'save'}<LoaderCircle size={14} class="animate-spin" />{/if} Save</Button>
          <Button variant="outline" onclick={sendTest} disabled={!!cfgBusy || (!alerts.push && !alerts.email)}>{#if cfgBusy === 'test'}<LoaderCircle size={14} class="animate-spin" />{/if} Send a test now</Button>
        </div>
      </section>

      <section class="space-y-4 border-t border-border pt-6" aria-labelledby="cal-h">
        <h3 id="cal-h" class="flex items-center gap-2 font-semibold"><CalendarPlus size={16} /> Calendar</h3>
        <p class="text-sm text-muted-foreground">Every launch day as an all-day event in Google, Apple or Outlook calendar.</p>
        <div class="flex flex-wrap gap-2">
          <a href="/api/drops/calendar.ics" download="drops.ics" class="inline-flex h-9 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-4 text-sm font-medium shadow-xs transition-colors hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/50"><CalendarPlus size={16} /> Download all drops (.ics)</a>
        </div>
        <div class="rounded-lg border border-border p-4">
          <p class="text-sm font-medium">Keep your calendar up to date</p>
          <p class="mt-1 text-sm text-muted-foreground">A private link your calendar app checks every few hours, so a changed date moves the event. In Google Calendar choose <em>Other calendars → From URL</em>. Anyone with the link can see your launch dates, so keep it to yourself.</p>
          {#if cfg.calendarUrl}
            <div class="mt-3 flex gap-2">
              <input id="cal-url" readonly value={cfg.calendarUrl} class={inputCls} onfocus={(e) => e.currentTarget.select()} aria-label="Calendar link" />
              <Button variant="outline" size="icon" onclick={copyLink} aria-label="Copy the link"><Copy size={15} /></Button>
            </div>
            <div class="mt-3 flex flex-wrap gap-2"><Button variant="outline" size="sm" onclick={makeLink} disabled={!!cfgBusy}>Make a new link</Button><Button variant="ghost" size="sm" class="text-destructive hover:text-destructive" onclick={stopLink} disabled={!!cfgBusy}>Turn the link off</Button></div>
          {:else}
            <Button class="mt-3" variant="outline" onclick={makeLink} disabled={!!cfgBusy}>{#if cfgBusy === 'link'}<LoaderCircle size={14} class="animate-spin" />{/if} Make a calendar link</Button>
          {/if}
        </div>
      </section>
    </div>
  {:else}
    <div class="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground" role="status"><LoaderCircle size={16} class="animate-spin" /> Loading…</div>
  {/if}
</Sheet>
